import { useTranslation } from 'react-i18next'
import { ErrorState, errorMessage } from '@/components/common/States'
import { useAgents } from '@/hooks/useDirectory'
import { useTicketAudit, useVerifyAudit } from '@/hooks/useTickets'
import { dateTimeLabel } from '@/lib/threadView'
import { cn } from '@/lib/utils'

function compact(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  const json = JSON.stringify(value)
  return json.length > 120 ? `${json.slice(0, 117)}…` : json
}

/** Audit trail tab: the hash-chained entries plus "Verify chain". */
export function AuditTab({ routeId, timeZone }: { routeId: string; timeZone: string }) {
  const { t } = useTranslation()
  const audit = useTicketAudit(routeId, true)
  const verify = useVerifyAudit(routeId)
  const agents = useAgents()
  const nameOf = (id: string | null, kind: string) =>
    (id && agents.data?.find((a) => a.id === id)?.name) || (id ? `${kind} ${id.slice(0, 8)}` : kind)

  const head = 'px-2 py-2 text-left text-eyebrow font-medium uppercase text-ink-muted'
  const cell = 'px-2 py-2 align-top text-label text-ink-body'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => verify.mutate()}
          disabled={verify.isPending}
          className="rounded-md border border-line-strong px-3.5 py-2 text-meta font-medium text-ink hover:bg-surface-subtle disabled:opacity-60"
        >
          {verify.isPending ? t('audit.verifying') : t('audit.verify')}
        </button>
        {verify.data ? (
          <p
            role="status"
            className={cn('text-meta', verify.data.valid ? 'text-resolved-heading' : 'font-medium text-status-escalated-fg')}
          >
            {verify.data.valid
              ? t('audit.valid', { length: verify.data.length, hash: (verify.data.headHash ?? '—').slice(0, 16) })
              : t('audit.invalid', { seq: verify.data.brokenAtSeq ?? '?', reason: verify.data.reason ?? '' })}
            {verify.data.valid && verify.data.anchor?.matches ? ` · ${t('audit.anchored', { day: verify.data.anchor.day })}` : ''}
          </p>
        ) : verify.error ? (
          <p role="alert" className="text-meta text-status-escalated-fg">
            {errorMessage(verify.error)}
          </p>
        ) : null}
      </div>

      {audit.isPending ? (
        <p className="text-meta text-ink-muted">{t('common.loading')}</p>
      ) : audit.isError ? (
        <ErrorState message={t('audit.loadError', { message: errorMessage(audit.error) })} onRetry={() => void audit.refetch()} />
      ) : audit.data.length === 0 ? (
        <p className="text-meta text-ink-muted">{t('audit.empty')}</p>
      ) : (
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-surface-subtle">
            <tr>
              <th className={cn(head, 'w-10')}>{t('audit.columns.seq')}</th>
              <th className={cn(head, 'w-[170px]')}>{t('audit.columns.at')}</th>
              <th className={cn(head, 'w-[150px]')}>{t('audit.columns.event')}</th>
              <th className={head}>{t('audit.columns.change')}</th>
              <th className={cn(head, 'w-[120px]')}>{t('audit.columns.actor')}</th>
              <th className={cn(head, 'w-[90px]')}>{t('audit.columns.hash')}</th>
            </tr>
          </thead>
          <tbody>
            {audit.data.map((e) => (
              <tr key={e.id} className="border-b border-line">
                <td className={cn(cell, 'font-mono')}>{e.seq}</td>
                <td className={cn(cell, 'font-mono')}>{dateTimeLabel(e.at, timeZone)}</td>
                <td className={cell}>
                  <span className="text-ink-strong">{e.eventType}</span>
                  {e.field ? <span className="text-ink-muted"> · {e.field}</span> : null}
                  {e.visibility === 'internal' ? <span className="ml-1 rounded-sm bg-note-bg px-1 text-note-heading">{t('audit.internal')}</span> : null}
                </td>
                <td className={cn(cell, 'break-words font-mono')}>
                  {e.oldValue !== null && e.oldValue !== undefined ? `${compact(e.oldValue)} → ` : ''}
                  {compact(e.newValue)}
                  {e.reason ? <span className="block font-sans text-ink-muted">{e.reason}</span> : null}
                </td>
                <td className={cn(cell, 'truncate')}>{nameOf(e.actorId, e.actorKind)}</td>
                <td className={cn(cell, 'truncate font-mono text-ink-muted')} title={e.hash}>
                  {e.hash.slice(0, 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
